import { Form } from 'antd'
import { AxiosResponse } from 'axios'
import { BUTTON_TYPES, Button } from 'components/Button'
import { CustomForm } from 'components/Forms/CustomForm'
import { notification } from 'components/Notification'
import Popup from 'components/Popup/Popup'
import Select from 'components/Select/Select'
import { TOptionWithSubtitle } from 'components/Select/types'
import Typography from 'components/Typography/Typography'
import { TYPOGRAPHY_WEIGHT } from 'components/Typography/constants'
import { BTN_TXT, PLACEHOLDERS, VALIDATION_MESSAGES } from 'constants/txt'
import { formatBankAccounName } from 'features/BankAccount/helpers'
import { useFinancialsPopup } from 'features/Financials/Providers/FinancialsPopupProvider'
import { useFinancialsRebatesContext } from 'features/Financials/Providers/FinancialsRebatesProvider'
import { validateMessages } from 'helper/common'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useRouter from 'hooks/useRouter'
import { routes } from 'router/Config/config.routes'
import { getUser } from 'redux/store/user/getters'
import { setLoading } from 'redux/store/common/slice'
import { completeRebatesPayment, fetchPaymentOptions } from './api'

const FORM_ID = 'complete-rebates-payment-form'

type TFormValues = {
  bank_account: string
}

type TProps = {
  onResetSelection: () => void
}

export const CompleteRebatesPopup = ({ onResetSelection }: TProps) => {
  const {
    completeRebatesPaymentPopup: { actions, state }
  } = useFinancialsPopup()
  const dispatch = useDispatch()
  const { navigate } = useRouter()
  const user = useSelector(getUser)
  const [form] = Form.useForm<TFormValues>()
  const {
    actions: { updateRebatesData }
  } = useFinancialsRebatesContext()

  const [paymentOptions, setPaymentOptions] = useState<TOptionWithSubtitle[]>(
    []
  )
  const firstOptionValue = paymentOptions[0]?.value
  const selectedBankAccount = Form.useWatch('bank_account', form)

  const { row, completeRebatesPaymentData } = state.payload

  const onCancel = useCallback(() => {
    form.resetFields()
    actions.close()
  }, [actions, form])

  const onSave = useCallback(
    async ({ bank_account }: TFormValues) => {
      if (!row?.uuid || !bank_account) return
      try {
        dispatch(setLoading(true))
        await completeRebatesPayment(row.uuid, {
          hospital_receivable_bank_account_id: bank_account
        })
        notification.success({
          message: VALIDATION_MESSAGES.REBATES_PAYMENT_WAS_SUBMITTED
        })
        form.resetFields()
        actions.close()
        onResetSelection()
        await updateRebatesData()
      } catch (error) {
        const typedResponse = error as AxiosResponse
        Object.values(typedResponse.data).forEach((error) => {
          notification.error({ message: error as string })
        })
      } finally {
        dispatch(setLoading(false))
        actions.close()
      }
    },
    [actions, dispatch, form, onResetSelection, row?.uuid, updateRebatesData]
  )

  const getPaymentOptions = useCallback(async (businessAccountId: string) => {
    const {
      data: { results }
    } = await fetchPaymentOptions(businessAccountId)
    setPaymentOptions(
      results.map((account) => ({
        label: formatBankAccounName(account),
        subtitle: account.bank_name,
        value: account.uuid
      }))
    )
  }, [])

  const goToBankAccounts = useCallback(() => {
    if (!user.health_system) return
    actions.close()
    navigate(
      `${routes.myCommunity}${routes.myOrganization}${routes.healthSystems}/${user.health_system}${routes.bankAccounts}`
    )
  }, [actions, navigate, user.health_system])

  useEffect(() => {
    if (!completeRebatesPaymentData?.business_account_id) return
    getPaymentOptions(completeRebatesPaymentData.business_account_id)
  }, [completeRebatesPaymentData?.business_account_id, getPaymentOptions])

  useEffect(() => {
    if (state.visible && firstOptionValue) {
      form.setFieldsValue({
        bank_account: String(firstOptionValue)
      })
    }
  }, [firstOptionValue, form, state.visible])

  const reason = useMemo(() => {
    if (!row || !row?.transfers) return ''
    return row.transfers
      ?.filter((transfer) => transfer.status !== 'completed')
      ?.map((transfer) => transfer.destination.issue_label)
      ?.join(', ')
  }, [row])

  if (!row || !completeRebatesPaymentData) return null

  return (
    <Popup
      {...state}
      zIndex={10000}
      footer={
        <>
          <Button onClick={onCancel} type={BUTTON_TYPES.DEFAULT} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            htmlType="submit"
            form={FORM_ID}
            upperCase
            disabled={!selectedBankAccount}
          >
            {BTN_TXT.SAVE}
          </Button>
        </>
      }
    >
      <CustomForm<TFormValues>
        form={form}
        id={FORM_ID}
        onFinish={onSave}
        validateMessages={validateMessages}
      >
        <Typography.Body2 className="grey-text">
          to {row.hospital_name}
        </Typography.Body2>
        <div className="column gap-16 mt-16">
          <div className="column">
            <Typography.Body1>
              The rebate has been partially paid. {reason}. Please select a
              receivable account to proceed.
            </Typography.Body1>
          </div>
          <div className="column">
            <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
              Receivable account
            </Typography.Body1>
            <Typography.Body2 className="grey-text">
              Manage hospital connected accounts in
              <Button type={BUTTON_TYPES.LINK} onClick={goToBankAccounts}>
                Bank accounts
              </Button>
            </Typography.Body2>
          </div>
          <Select.WithSubtitle
            options={paymentOptions}
            propsItem={{
              name: 'bank_account',
              rules: [
                {
                  required: true
                }
              ]
            }}
            propsSelect={{
              placeholder: PLACEHOLDERS.PLEASE_SELECT
            }}
            alwaysName
          />
        </div>
      </CustomForm>
    </Popup>
  )
}
